import React, { useMemo, useState, FormEvent, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useNavigationStore } from "@/stores/navigation"
import { getValidUserAccessData, saveUserAccessData, isUserAccessValid } from "@/utils/userAccess"
import { emailService } from "@/services/emailService"

const UseCase: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const navigationStore = useNavigationStore()

  type UseCase = {
    slug: string
    title: string
    client: string
    product: string | string[]
    industry: string
    outcome: string
    summary?: string
  }

  const [search, setSearch] = useState("")
  const [productFilter, setProductFilter] = useState("")
  const [industryFilter, setIndustryFilter] = useState("")
  const [orgFilter, setOrgFilter] = useState("")

  const [formOpen, setFormOpen] = useState(false)
  const [selectedCase, setSelectedCase] = useState<UseCase | null>(null)
  const [simulateEU, setSimulateEU] = useState(false)
  const [pendingDoubleOptIn, setPendingDoubleOptIn] = useState(false)

  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [org, setOrg] = useState("")
  const [phone, setPhone] = useState("")
  const [errors, setErrors] = useState<{
    name?: string
    email?: string
    org?: string
  }>({})

  // Organization alias mapping - maps aliases to the main client name
  const orgAliases: Record<string, string> = {
    // Dana-Farber Cancer Institute aliases
    dfci: "Dana-Farber Cancer Institute",
    "dana-farber": "Dana-Farber Cancer Institute",
    "dana farber": "Dana-Farber Cancer Institute",

    // CHOC Children's Hospital aliases
    choc: "CHOC Children's Hospital",
    "choc childrens": "CHOC Children's Hospital",
    "choc children's": "CHOC Children's Hospital",

    // Louisiana Department of Corrections aliases
    Ldoc: "Louisiana DOC",
    "la doc": "Louisiana DOC",
    "louisiana department of corrections": "Louisiana DOC",

    // TELUS Health aliases
    telus: "TELUS Health Care Centres",
    "telus health": "TELUS Health Care Centres",

    // Newfoundland & Labrador Centre for Health Information aliases
    nlchi: "Newfoundland & Labrador Centre for Health Information",
    "newfoundland labrador":
      "Newfoundland & Labrador Centre for Health Information",
    "nl centre": "Newfoundland & Labrador Centre for Health Information",

    // Southern Health aliases
    "southern health": "Southern Health & Social Care Trust",
    "southern health trust": "Southern Health & Social Care Trust",

    // CHI St. Luke's aliases
    STLUKE: "CHI St. Luke's",
    "chi st. lukes": "CHI St. Luke's",
    "chi st luke": "CHI St. Luke's",

    // Lincolnshire aliases
    lincolnshire: "Lincolnshire NHS CCG",
    "lincolnshire ccg": "Lincolnshire NHS CCG",
    "lincolnshire nhs": "Lincolnshire NHS CCG",

    // Vail Health aliases
    vail: "Vail Health",
    VAILHEALTH: "Vail Health",

    // CritiCall Ontario aliases
    criticall: "CritiCall Ontario",
    "criticall ontario": "CritiCall Ontario",
  }

  const useCases: UseCase[] = [
    {
      slug: "critical-ontario",
      title: "CritiCall Ontario",
      client: "CritiCall Ontario",
      product: [
        t("useCaseFinder.products.referralcx"),
        t("useCaseFinder.products.casecx"),
      ],
      industry: t("useCaseFinder.industries.hospitals"),
      outcome: "",
      summary:
        "Streamlined province-wide referral management—connecting patients to the right care, faster.",
    },
    {
      slug: "dfci-cell-therapy",
      title: t("useCaseFinder.useCases.dfciCellTherapy.title"),
      client: t("useCaseFinder.useCases.dfciCellTherapy.client"),
      product: [
        t("useCaseFinder.products.referralcx"),
        t("useCaseFinder.products.schedulingcx"),
        t("useCaseFinder.products.cellcx"),
      ],
      industry: t("useCaseFinder.industries.research"),
      outcome: t("useCaseFinder.useCases.dfciCellTherapy.outcome"),
      summary:
        "Powering complex oncology referrals with secure, scalable workflows for global leaders in cancer care.",
    },
    {
      slug: "vail-health-covid-programs",
      title: "Vail Health",
      client: "Vail Health",
      product: [
        t("useCaseFinder.products.vaxmanagercx"),
        t("useCaseFinder.products.consumercx"),
      ],
      industry: t("useCaseFinder.industries.hospitals"),
      outcome: "",
      summary:
        "Unified referral and scheduling platform that enhances access and efficiency for a growing health system.",
    },
    {
      slug: "chi-st-lukes-international",
      title: "CHI St. Luke's",
      client: "CHI St. Luke's",
      product: [
        t("useCaseFinder.products.econsultcx"),
        t("useCaseFinder.products.referralcx"),
      ],
      industry: t("useCaseFinder.industries.hospitals"),
      outcome: "",
      summary:
        "Driving international referrals and boosting revenue with coordinated case and patient management.",
    },
    {
      slug: "choc-pediatric-referrals",
      title: "CHOC Children's Hospital",
      client: "CHOC Children's Hospital",
      product: [
        t("useCaseFinder.products.referralcx"),
        t("useCaseFinder.products.schedulingcx"),
      ],
      industry: t("useCaseFinder.industries.hospitals"),
      outcome: "",
      summary:
        "Transforming pediatric specialty referrals into a seamless experience for providers and families.",
    },
    {
      slug: "louisiana-doc",
      title: "Louisiana Department of Corrections",
      client: "Louisiana DOC",
      product: [
        t("useCaseFinder.products.referralcx"),
        t("useCaseFinder.products.casecx"),
        t("useCaseFinder.products.schedulingcx"),
      ],
      industry: t("useCaseFinder.industries.corrections"),
      outcome: "",
      summary:
        "Modernizing correctional healthcare with integrated referral, eConsult, and scheduling workflows.",
    },
    {
      slug: "nlchi-vascular-imaging",
      title: "NLCHI",
      client: "Newfoundland & Labrador Centre for Health Information",
      product: [
        t("useCaseFinder.products.schedulingcx"),
        t("useCaseFinder.products.referralcx"),
      ],
      industry: t("useCaseFinder.industries.publicHealth"),
      outcome: "",
      summary:
        "Leading Canada's telehealth expansion with integrated scheduling and referral technology.",
    },
    {
      slug: "lincolnshire-each",
      title: "Lincolnshire (UK)",
      client: "Lincolnshire NHS CCG",
      product: [
        t("useCaseFinder.products.referralcx"),
        t("useCaseFinder.products.schedulingcx"),
      ],
      industry: t("useCaseFinder.industries.hospitals"),
      outcome: "",
      summary:
        "Cutting referral processing times to just 7 days with streamlined digital intake and tracking.",
    },
    {
      slug: "southern-health-dermatology",
      title: "Southern Health & Social Care Trust",
      client: "Southern Health & Social Care Trust",
      product: [
        t("useCaseFinder.products.econsultcx"),
        t("useCaseFinder.products.referralcx"),
      ],
      industry: t("useCaseFinder.industries.hospitals"),
      outcome: "",
      summary:
        "Delivering faster dermatology access with eConsults and referral triage—patients seen sooner, closer to home.",
    },
    {
      slug: "telus-health-corporate",
      title: "TELUS Health Care Centres",
      client: "TELUS Health Care Centres",
      product: [
        t("useCaseFinder.products.consumercx"),
        t("useCaseFinder.products.schedulingcx"),
      ],
      industry: t("useCaseFinder.industries.retailCare"),
      outcome: "",
      summary:
        "Scaling corporate health scheduling nationwide with a multilingual, self-service employer portal.",
    },
  ]

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return useCases.filter((uc) => {
      const productArray = Array.isArray(uc.product) ? uc.product : [uc.product]
      const productString = productArray.join(" ")

      const matchesSearch =
        !q ||
        uc.title.toLowerCase().includes(q) ||
        uc.client.toLowerCase().includes(q) ||
        uc.outcome.toLowerCase().includes(q) ||
        productString.toLowerCase().includes(q)
      const matchesProduct =
        !productFilter || productArray.includes(productFilter)
      const matchesIndustry = !industryFilter || uc.industry === industryFilter

      // Enhanced organization matching with aliases
      const matchesOrg =
        !orgFilter ||
        (() => {
          const orgFilterLower = orgFilter.toLowerCase()
          const clientLower = uc.client.toLowerCase()

          // Direct match with client name
          if (clientLower.includes(orgFilterLower)) {
            return true
          }

          // Check if the filter matches any alias for this client
          const matchingAlias = Object.entries(orgAliases).find(
            ([alias, mappedClient]) =>
              alias.toLowerCase().includes(orgFilterLower) &&
              mappedClient === uc.client
          )

          if (matchingAlias) {
            return true
          }

          // Check if the filter is an alias that maps to this client
          const aliasMatch = orgAliases[orgFilterLower]
          if (aliasMatch && aliasMatch === uc.client) {
            return true
          }

          return false
        })()

      return matchesSearch && matchesProduct && matchesIndustry && matchesOrg
    })
  }, [search, productFilter, industryFilter, orgFilter, useCases, orgAliases])

  // Initialize filters from URL query params (e.g., ?product=referralcx, ?Org=CHOC)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)

    // Handle product filter
    const productParam = params.get("product")
    if (productParam) {
      // Map param like 'referralcx' to localized label used in options
      const productLabelMap: Record<string, string> = {
        referralcx: t("useCaseFinder.products.referralcx"),
        cellcx: t("useCaseFinder.products.cellcx"),
        schedulingcx: t("useCaseFinder.products.schedulingcx"),
        casecx: t("useCaseFinder.products.casecx"),
        econsultcx: t("useCaseFinder.products.econsultcx"),
        probationcx: t("useCaseFinder.products.probationcx"),
        vaxmanagercx: t("useCaseFinder.products.vaxmanagercx"),
        consumercx: t("useCaseFinder.products.consumercx"),
      }
      const label = productLabelMap[productParam.toLowerCase()]
      if (label) setProductFilter(label)
    }

    // Handle organization filter
    const orgParam = params.get("Org") || params.get("org")
    if (orgParam) {
      setOrgFilter(orgParam)
    }
  }, [t])

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)

    // Update product parameter
    if (productFilter) {
      const productKeyMap: Record<string, string> = {
        [t("useCaseFinder.products.referralcx")]: "referralcx",
        [t("useCaseFinder.products.cellcx")]: "cellcx",
        [t("useCaseFinder.products.schedulingcx")]: "schedulingcx",
        [t("useCaseFinder.products.casecx")]: "casecx",
        [t("useCaseFinder.products.econsultcx")]: "econsultcx",
        [t("useCaseFinder.products.probationcx")]: "probationcx",
        [t("useCaseFinder.products.vaxmanagercx")]: "vaxmanagercx",
        [t("useCaseFinder.products.consumercx")]: "consumercx",
      }
      const productKey = productKeyMap[productFilter]
      if (productKey) {
        params.set("product", productKey)
      }
    } else {
      params.delete("product")
    }

    // Update organization parameter
    if (orgFilter) {
      params.set("Org", orgFilter)
    } else {
      params.delete("Org")
    }

    // Update URL without causing a page reload
    const newUrl = `${window.location.pathname}${params.toString() ? "?" + params.toString() : ""}`
    window.history.replaceState({}, "", newUrl)
  }, [productFilter, orgFilter, t])

  const handleViewCase = (uc: UseCase) => {
    setSelectedCase(uc)
    
    // Check if user has valid access
    const validAccess = isUserAccessValid()
    
    if (validAccess) {
      // User has valid access, navigate directly to the case study
      navigate(`/use-case-detail/${uc.slug}`)
    } else {
      // User needs to fill out form
      setFormOpen(true)
      
      // Check for existing user data and pre-fill form
      const userData = getValidUserAccessData()
      if (userData) {
        setName(userData.name)
        setEmail(userData.email)
        setOrg(userData.organization)
        setPhone(userData.phone || "")
      }
    }
  }

  const isValidEmail = (v: string) => /\S+@\S+\.\S+/.test(v)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const nextErrors: typeof errors = {}
    if (!name.trim())
      nextErrors.name = t("validation.required").replace("{type}", "name")
    if (!email.trim())
      nextErrors.email = t("validation.required").replace("{type}", "email")
    else if (!isValidEmail(email)) nextErrors.email = t("validation.email")
    if (!org.trim())
      nextErrors.org = t("validation.required").replace(
        "{type}",
        "organization"
      )
    setErrors(nextErrors)

    if (Object.keys(nextErrors).length > 0 || !selectedCase) return

    if (simulateEU) {
      setPendingDoubleOptIn(true)
      return
    }

    try {
      // Send to email service
      const result = await emailService.sendDownloadForm({
        name,
        email,
        organization: org,
        phone: phone || undefined,
        paperTitle: `${selectedCase.client} Case Study`,
        pdfPath: `/PDF/CaseStudies/${selectedCase.slug}.pdf`
      }, `case_study_access_${selectedCase.slug}`)
      
      if (result.success) {
        // Save user data to cookie
        saveUserAccessData({
          name,
          email,
          organization: org,
          phone: phone || undefined
        })
        
      } else {
        console.error('Failed to send case study form:', result.message)
        // Still allow access but log the error
        saveUserAccessData({
          name,
          email,
          organization: org,
          phone: phone || undefined
        })
        
      }
    } catch (error) {
      console.error('Error submitting case study form:', error)
      // Fallback: still allow access
      saveUserAccessData({
        name,
        email,
        organization: org,
        phone: phone || undefined
      })
      
    }

    navigate(`/use-case-detail/${selectedCase.slug}`)
    closeAndReset()
  }

  const closeAndReset = () => {
    setFormOpen(false)
    setSelectedCase(null)
    setName("")
    setEmail("")
    setOrg("")
    setPhone("")
    setErrors({})
    setPendingDoubleOptIn(false)
  }

  const handleBookDemo = () => {
    navigationStore.openDemoModal()
  }

  return (
    <div className="w-full bg-white text-slate-800">
      {/* HERO */}
      <header
        className="w-full relative"
        style={{
          background: "linear-gradient(90deg, #6A11CB 0%, #2575FC 100%)",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 text-white relative z-10">
          <div className="mb-6 sm:mb-8">
            <h2 className="text-6xl sm:text-7xl md:text-8xl leading-none tracking-tight">
              <span>{t("common.company")}</span>
              <span className="font-bold text-[#7AC943]">CX</span>
              <sup className="align-super text-2xl sm:text-3xl md:text-4xl ml-1">
                ™
              </sup>
            </h2>
          </div>
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
              {t("useCaseFinder.hero.title")}
            </h1>
            <p className="text-base sm:text-lg md:text-xl/8 opacity-95 mb-6">
              {t("useCaseFinder.hero.subtitle")}
            </p>
            <div className="max-w-xl">
              <input
                className="w-full rounded-lg px-4 py-3 bg-white text-gray-900 placeholder:text-gray-500 shadow-sm"
                placeholder={t("useCaseFinder.hero.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div
          className="absolute inset-0 bg-[url('/textures/pattern.svg')] opacity-10 mix-blend-overlay pointer-events-none"
          aria-hidden="true"
        />
      </header>

      {/* FILTERS (sticky) */}
      <section className="sticky top-0 z-30 py-3 px-4 bg-gray-50/95 supports-[backdrop-filter]:bg-gray-50/80 backdrop-blur border-b shadow-sm">
        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <select
            className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2"
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
          >
            <option value="">{t("useCaseFinder.filters.productFilter")}</option>
            <option value={t("useCaseFinder.products.referralcx")}>
              {t("useCaseFinder.products.referralcx")}
            </option>
            <option value={t("useCaseFinder.products.cellcx")}>
              {t("useCaseFinder.products.cellcx")}
            </option>
            <option value={t("useCaseFinder.products.schedulingcx")}>
              {t("useCaseFinder.products.schedulingcx")}
            </option>
            <option value={t("useCaseFinder.products.casecx")}>
              {t("useCaseFinder.products.casecx")}
            </option>
            <option value={t("useCaseFinder.products.econsultcx")}>
              {t("useCaseFinder.products.econsultcx")}
            </option>
            <option value={t("useCaseFinder.products.probationcx")}>
              {t("useCaseFinder.products.probationcx")}
            </option>
            <option value={t("useCaseFinder.products.vaxmanagercx")}>
              {t("useCaseFinder.products.vaxmanagercx")}
            </option>
            <option value={t("useCaseFinder.products.consumercx")}>
              {t("useCaseFinder.products.consumercx")}
            </option>
          </select>

          <select
            className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2"
            value={industryFilter}
            onChange={(e) => setIndustryFilter(e.target.value)}
          >
            <option value="">
              {t("useCaseFinder.filters.industryFilter")}
            </option>
            <option value={t("useCaseFinder.industries.hospitals")}>
              {t("useCaseFinder.industries.hospitals")}
            </option>
            <option value={t("useCaseFinder.industries.publicHealth")}>
              {t("useCaseFinder.industries.publicHealth")}
            </option>
            <option value={t("useCaseFinder.industries.retailCare")}>
              {t("useCaseFinder.industries.retailCare")}
            </option>
            <option value={t("useCaseFinder.industries.corrections")}>
              {t("useCaseFinder.industries.corrections")}
            </option>
            <option value={t("useCaseFinder.industries.research")}>
              {t("useCaseFinder.industries.research")}
            </option>
          </select>

          <input
            className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2"
            placeholder="Filter by Organization"
            value={orgFilter}
            onChange={(e) => setOrgFilter(e.target.value)}
          />

          <div className="flex gap-3">
            <button
              className="w-full sm:w-auto rounded-lg border border-slate-300 px-4 py-2 bg-white hover:bg-slate-50"
              onClick={() => {
                setSearch("")
                setProductFilter("")
                setIndustryFilter("")
                setOrgFilter("")
              }}
            >
              {t("useCaseFinder.filters.clearFilters")}
            </button>
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="py-8 px-6">
        <div className="max-w-6xl mx-auto grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((uc) => (
            <div
              key={uc.slug}
              className="rounded-2xl border border-slate-200 shadow-sm bg-white/80 flex flex-col"
            >
              <div className="p-6 flex flex-col flex-grow">
                <div className="flex flex-wrap gap-2 mb-3">
                  {(Array.isArray(uc.product) ? uc.product : [uc.product]).map(
                    (product, index) => (
                      <div
                        key={index}
                        className="text-xs font-medium bg-blue-100 text-blue-800 rounded px-2 py-1"
                      >
                        {product}
                      </div>
                    )
                  )}
                </div>
                <h3 className="text-lg font-semibold mb-1 text-slate-900">
                  {uc.title}
                </h3>
                <p className="text-sm text-slate-500 mb-2">{uc.client}</p>
                {uc.summary ? (
                  <p className="text-slate-700 mb-4 flex-grow">{uc.summary}</p>
                ) : (
                  <p className="text-slate-700 mb-4 flex-grow">{uc.outcome}</p>
                )}
              </div>
              <div className="p-6 pt-0">
                <button
                  className="w-full rounded-md bg-slate-200 text-slate-800 hover:bg-slate-300 px-4 py-2"
                  onClick={() => handleViewCase(uc)}
                >
                  {t("common.viewCaseStudy")}
                </button>
              </div>
            </div>
          ))}
        </div>
        {filtered.length === 0 && (
          <div className="max-w-6xl mx-auto text-center text-gray-600 py-10">
            {t("useCaseFinder.actions.noResults")}
          </div>
        )}
      </section>

      {/* MID-PAGE CTA */}
      <section className="py-12 px-6 bg-gradient-to-r from-[#F9FAFB] to-[#F3F4F6] border-t border-b border-slate-200">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">
              {t("useCaseFinder.midPageCta.title")}
            </h3>
            <p className="text-slate-600">
              {t("useCaseFinder.midPageCta.subtitle")}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={handleBookDemo}
              className="w-full sm:w-auto text-center bg-[#6A11CB] text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:opacity-90 transition"
            >
              {t("common.bookDemo")}
            </button>
            <button
              onClick={() => navigationStore.openContactModal()}
              className="w-full sm:w-auto text-center bg-white text-[#6A11CB] font-semibold px-6 py-3 rounded-lg border border-[#6A11CB]/30 hover:bg-gray-50 transition"
            >
              {t("useCaseFinder.midPageCta.contactUs")}
            </button>
          </div>
        </div>
      </section>

      {/* MODAL */}
      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl p-8">
            <h2 className="text-2xl font-bold mb-2 text-slate-900">
              {t("useCaseFinder.modal.title")}
            </h2>
            <p className="text-slate-600 mb-6">
              {t("useCaseFinder.modal.subtitle")}
            </p>
            <form
              className="flex flex-col gap-4"
              onSubmit={onSubmit}
              noValidate
            >
              <div>
                <input
                  className="w-full rounded-lg border px-3 py-2"
                  placeholder={t("useCaseFinder.modal.form.name")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>
              <div>
                <input
                  className="w-full rounded-lg border px-3 py-2"
                  type="email"
                  placeholder={t("useCaseFinder.modal.form.email")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>
              <div>
                <input
                  className="w-full rounded-lg border px-3 py-2"
                  placeholder={t("useCaseFinder.modal.form.organization")}
                  value={org}
                  onChange={(e) => setOrg(e.target.value)}
                />
                {errors.org && (
                  <p className="mt-1 text-sm text-red-600">{errors.org}</p>
                )}
              </div>
              <div>
                <input
                  className="w-full rounded-lg border px-3 py-2"
                  type="tel"
                  placeholder={t("useCaseFinder.modal.form.phone")}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={simulateEU}
                  onChange={(e) => setSimulateEU(e.target.checked)}
                />
                {t("useCaseFinder.modal.form.simulateEU")}
              </label>
              <button className="rounded-lg bg-[#6A11CB] text-white px-4 py-2 font-semibold">
                {t("useCaseFinder.modal.form.submit")}
              </button>
            </form>
            {pendingDoubleOptIn && (
              <div className="mt-6 rounded-lg border border-slate-200 p-4 bg-slate-50">
                <p className="text-sm text-slate-700">
                  {t("useCaseFinder.modal.euConfirmation.title")}
                </p>
                <div className="mt-3 flex gap-3">
                  <button
                    className="rounded border px-3 py-2"
                    onClick={() => {
                      if (!selectedCase) return
                      navigate(`/use-case-detail/${selectedCase.slug}`)
                      closeAndReset()
                    }}
                  >
                    {t("useCaseFinder.modal.euConfirmation.simulateConfirm")}
                  </button>
                  <button
                    className="rounded px-3 py-2"
                    onClick={() => setPendingDoubleOptIn(false)}
                  >
                    {t("useCaseFinder.modal.euConfirmation.hide")}
                  </button>
                </div>
              </div>
            )}
            <button
              className="mt-4 text-slate-500 hover:text-slate-700 text-sm"
              onClick={closeAndReset}
            >
              {t("useCaseFinder.modal.cancel")}
            </button>
          </div>
        </div>
      )}

      {/* FOOTER CTA */}
      <section id="contact" className="relative mt-2">
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(90deg, #6A11CB 0%, #2575FC 100%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 text-white">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-center">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">
                {t("useCaseFinder.footerCta.title")}
              </h3>
              <p className="opacity-90 text-base sm:text-lg">
                {t("useCaseFinder.footerCta.subtitle")}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 lg:justify-end w-full sm:w-auto">
              <button
                onClick={handleBookDemo}
                className="w-full sm:w-auto text-center bg-white text-[#6A11CB] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-gray-100 transition"
              >
                {t("common.bookDemo")}
              </button>
              <a
                href="#"
                className="w-full sm:w-auto text-center bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/20 transition"
              >
                {t("useCaseFinder.footerCta.seeClientStories")}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default UseCase
